import { useState, useCallback } from 'react';
import { post } from '../../post';

/**
 * Hook personnalisé pour la logique de connexion
 * Gère l'état du formulaire, la validation et l'authentification
 * 
 * @param {Object} params - Paramètres du hook
 * @param {Function} params.t - Fonction de traduction
 * @returns {Object} État et handlers pour le composant Login
 */
export const useLoginLogic = ({ t }) => {
  // État du formulaire
  const [state, setState] = useState({
    email: '',
    password: '',
    err: undefined,
    loading: false
  });

  /**
   * Met à jour un champ du formulaire
   * @param {string} field - Nom du champ à mettre à jour
   * @param {string} value - Nouvelle valeur
   */
  const updateField = useCallback((field, value) => {
    setState(prev => ({
      ...prev,
      [field]: value,
      err: undefined // Efface l'erreur lors de la saisie
    }));
  }, []);

  /**
   * Gère la soumission du formulaire de connexion
   * @param {Event} e - Événement de soumission
   */
  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    
    // Validation basique
    if (!state.email.trim() || !state.password.trim()) {
      setState(prev => ({
        ...prev,
        err: t('login.error.required')
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, err: undefined }));

    try {
      const res = await post("/login", {}, {
        login: state.email.trim(),
        password: state.password
      }, true);

      if (res.error) {
        console.log(">>> login err : ", res.error);
        setState(prev => ({
          ...prev,
          err: res.error,
          loading: false
        }));
        return;
      }

      console.log(">>> login ok");
      // Redirection après connexion réussie
      window.location.replace("/");
      
    } catch (err) {
      console.log(">>> login err : ", err);
      setState(prev => ({
        ...prev,
        err: err.message || t('login.error.generic'),
        loading: false
      }));
    }
  }, [state.email, state.password, t]);

  /**
   * Gère le changement de valeur des champs
   * @param {string} field - Nom du champ
   * @returns {Function} Handler pour onChange
   */
  const handleFieldChange = useCallback((field) => {
    return (e) => {
      updateField(field, e.target.value);
    };
  }, [updateField]);

  /**
   * Efface l'erreur actuelle
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, err: undefined }));
  }, []);

  return {
    state,
    handlers: {
      handleLogin,
      handleFieldChange,
      clearError
    },
    utils: {
      updateField
    }
  };
};
